/**
 * ArchiveView
 * Shows archived conversations across all types (DM, AI, ritual-group) with
 * dimmed styling and an unarchive action. Displayed inline within the sidebar.
 */

import { ArchiveRestore } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import { Avatar } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { useUpdateAiConversation } from "../../hooks/useAiChat"
import {
  useArchivedConversations,
  useUnarchiveConversation,
} from "../../hooks/useUnifiedConversations"
import type { UnifiedConversation } from "../../types/unified-chat.types"
import { getConversationRoute } from "../../utils/conversationRoutes"
import { formatTimeAgo } from "../../utils/formatTimeAgo"

function ArchivedIcon({ conversation }: { conversation: UnifiedConversation }) {
  if (conversation.type === "ai") {
    return (
      <img src={aiAvatarImg} alt="Rituality AI" className="size-[30px] rounded-full shrink-0" />
    )
  }
  return (
    <Avatar
      src={conversation.profilePhoto || undefined}
      alt={conversation.name}
      fallback={conversation.name || "?"}
      size="sm"
    />
  )
}

function ArchivedItem({ conversation }: { conversation: UnifiedConversation }) {
  const location = useLocation()
  const route = getConversationRoute(conversation)
  const isActive = location.pathname === route
  const unarchiveDm = useUnarchiveConversation()
  const updateAiConversation = useUpdateAiConversation()

  const handleUnarchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (conversation.type === "ai") {
      updateAiConversation.mutate(
        { id: conversation.id, data: { status: "active" } },
        {
          onSuccess: () => toast.success("Conversation unarchived"),
          onError: () => toast.error("Failed to unarchive conversation"),
        },
      )
    } else {
      unarchiveDm.mutate(conversation.id, {
        onSuccess: () => toast.success("Conversation unarchived"),
        onError: () => toast.error("Failed to unarchive conversation"),
      })
    }
  }

  return (
    <Link
      to={route}
      className={cn(
        "group relative w-full flex flex-col gap-[10px] px-[20px] py-[15px] text-left transition-colors no-underline border-b border-border opacity-50 hover:opacity-70",
        isActive && "bg-[var(--color-chat-active)] opacity-70",
      )}
    >
      <div className="flex items-center h-[30px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <ArchivedIcon conversation={conversation} />
          <p className="text-[13px] font-semibold text-foreground truncate">
            {conversation.name || "Unknown"}
          </p>
        </div>
        <span className="text-[13px] text-muted-foreground/70 shrink-0">
          {formatTimeAgo(conversation.lastMessageAt ?? conversation.createdAt)}
        </span>
      </div>

      {conversation.lastMessagePreview && (
        <p className="text-[13px] font-normal text-foreground leading-[25px] line-clamp-2">
          {conversation.lastMessagePreview}
        </p>
      )}

      <button
        type="button"
        onClick={handleUnarchive}
        aria-label="Unarchive conversation"
        className="absolute top-[15px] right-[20px] opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
        title="Unarchive"
      >
        <ArchiveRestore className="size-4" />
      </button>
    </Link>
  )
}

export function ArchiveView() {
  const { data, isLoading } = useArchivedConversations()

  return (
    <div className="flex-1 overflow-y-auto">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data?.conversations.length ? (
        <div className="text-center py-8 px-4">
          <p className="text-[13px] text-muted-foreground">No archived conversations</p>
        </div>
      ) : (
        data.conversations.map(conversation => (
          <ArchivedItem
            key={`${conversation.type}-${conversation.id}`}
            conversation={conversation}
          />
        ))
      )}
    </div>
  )
}
